import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldAlert, Home, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/app/store/authStore';
import { useRole } from '@/app/hooks/useRole';

/**
 * Unauthorized Page
 *
 * Displayed when a user tries to access a route they don't have permission for
 * Shows current role and provides navigation back to their appropriate dashboard
 */
const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { roleName, isClientManager, isAdmin } = useRole();

  const handleGoHome = () => {
    // Navigate to role-specific dashboard
    if (isClientManager) {
      navigate('/client-manager/dashboard');
    } else if (isAdmin) {
      navigate('/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex items-center justify-center min-h-[80vh] p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-destructive/10 p-4 dark:bg-destructive">
              <ShieldAlert className="h-12 w-12 text-destructive" />
            </div>
          </div>
          <CardTitle className="text-2xl">Access Denied</CardTitle>
          <CardDescription className="text-base">
            You don't have permission to access this page
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="bg-muted rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Current User:</span>
              <span className="font-medium">{user?.full_name || user?.username}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Role:</span>
              <span className="font-medium capitalize">
                {roleName?.replace('_', ' ') || 'Unknown'}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Organization:</span>
              <span className="font-medium">{user?.organization?.name || 'N/A'}</span>
            </div>
          </div>

          <p className="text-sm text-center text-muted-foreground">
            If you believe you should have access to this page, please contact your administrator.
          </p>
        </CardContent>

        <CardFooter className="flex gap-2">
          <Button variant="outline" onClick={handleGoBack} className="flex-1 gap-2">
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
          <Button onClick={handleGoHome} className="flex-1 gap-2">
            <Home className="h-4 w-4" />
            Go to Dashboard
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default UnauthorizedPage;
